import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Truck, ExternalLink, RefreshCw, Plus, Trash2, MapPin, Calendar, AlertCircle, CheckCircle2, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_STYLE = {
  pending:           { bg: 'bg-zinc-800',         text: 'text-zinc-300',  label: 'Pending',          icon: Clock },
  picked_up:         { bg: 'bg-blue-500/20',      text: 'text-blue-300',  label: 'Picked up',        icon: Truck },
  in_transit:        { bg: 'bg-amber-500/20',     text: 'text-amber-300', label: 'In transit',       icon: Truck },
  out_for_delivery:  { bg: 'bg-purple-500/20',    text: 'text-purple-300',label: 'Out for delivery', icon: Truck },
  delivered:         { bg: 'bg-green-500/20',     text: 'text-green-300', label: 'Delivered',        icon: CheckCircle2 },
  delayed:           { bg: 'bg-orange-500/20',    text: 'text-orange-300',label: 'Delayed',          icon: AlertCircle },
  exception:         { bg: 'bg-red-500/20',       text: 'text-red-300',   label: 'Exception',        icon: AlertCircle },
};

function StatusChip({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold ${s.bg} ${s.text}`}>
      <Icon size={10} />{s.label}
    </span>
  );
}

function NewDispatchForm({ kind, transporters, onCreated, onCancel }) {
  const [form, setForm] = useState({
    transporter_code: 'VRL',
    lr_number: '',
    tracking_url: '',
    ref_label: '',
    client_or_vendor: '',
    from_location: '',
    to_location: '',
    expected_delivery: '',
    notes: '',
  });
  const [busy, setBusy] = useState(false);
  const sel = transporters.find(t => t.code === form.transporter_code) || {};

  const submit = async () => {
    if (!form.lr_number.trim()) { toast.error('LR / docket number required'); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/tracking`, { kind, ...form }, { withCredentials: true });
      toast.success(`Tracking added · ${res.data.transporter_code} ${res.data.lr_number}`);
      onCreated?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 mb-4" data-testid="new-dispatch-form">
      <h3 className="text-zinc-200 text-sm font-semibold mb-3">
        New {kind === 'sales' ? 'Outbound (Sales)' : 'Inbound (Purchase)'} Dispatch
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <select value={form.transporter_code}
          onChange={e => setForm(f => ({ ...f, transporter_code: e.target.value }))}
          data-testid="transporter-select"
          className="bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm">
          {transporters.map(t => <option key={t.code} value={t.code}>{t.name}</option>)}
        </select>
        <input value={form.lr_number}
          onChange={e => setForm(f => ({ ...f, lr_number: e.target.value }))}
          placeholder={sel.lookup_hint || 'LR / docket number'}
          data-testid="lr-number-input"
          className="bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm" />
        <input value={form.ref_label}
          onChange={e => setForm(f => ({ ...f, ref_label: e.target.value }))}
          placeholder={kind === 'sales' ? 'SO / Invoice number' : 'PO number'}
          className="bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm" />
        <input value={form.client_or_vendor}
          onChange={e => setForm(f => ({ ...f, client_or_vendor: e.target.value }))}
          placeholder={kind === 'sales' ? 'Client name' : 'Vendor name'}
          className="bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm" />
        <input value={form.from_location}
          onChange={e => setForm(f => ({ ...f, from_location: e.target.value }))}
          placeholder="From (city)"
          className="bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm" />
        <input value={form.to_location}
          onChange={e => setForm(f => ({ ...f, to_location: e.target.value }))}
          placeholder="To (city)"
          className="bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm" />
        <input type="date" value={form.expected_delivery}
          onChange={e => setForm(f => ({ ...f, expected_delivery: e.target.value }))}
          className="bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm" />
        {form.transporter_code === 'OTHER' && (
          <input value={form.tracking_url}
            onChange={e => setForm(f => ({ ...f, tracking_url: e.target.value }))}
            placeholder="Custom tracking URL (https://…)"
            className="md:col-span-2 bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm" />
        )}
        <textarea value={form.notes}
          onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
          placeholder="Notes / driver phone / vehicle no"
          rows={1}
          className="md:col-span-3 bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm resize-none" />
      </div>
      <div className="flex items-center gap-2 mt-3">
        <button onClick={submit} disabled={busy}
          data-testid="dispatch-submit-btn"
          className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black text-xs font-semibold rounded-lg disabled:opacity-50">
          {busy ? 'Adding…' : 'Add tracking'}
        </button>
        <button onClick={onCancel}
          className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs rounded-lg">Cancel</button>
        {sel.lookup_hint && (
          <span className="text-zinc-500 text-[11px] ml-auto">Hint: {sel.lookup_hint}</span>
        )}
      </div>
    </div>
  );
}

export default function TrackingDispatches() {
  const [tab, setTab] = useState('sales');
  const [rows, setRows] = useState([]);
  const [transporters, setTransporters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [polling, setPolling] = useState(null);

  // Transporters are static — fetch once at mount, not on every tab switch.
  useEffect(() => {
    axios.get(`${API}/tracking/transporters`, { withCredentials: true })
      .then(r => setTransporters(r.data || []))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/tracking?kind=${tab}`, { withCredentials: true });
      setRows(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load tracking');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const pollOne = async (id) => {
    setPolling(id);
    try {
      await axios.post(`${API}/tracking/${id}/poll`, {}, { withCredentials: true });
      toast.success('Re-polled — refreshing');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Poll failed');
    } finally {
      setPolling(null);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/tracking/${id}`, { status }, { withCredentials: true });
      toast.success(`Marked ${status.replace(/_/g, ' ')}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this tracking entry?')) return;
    try {
      await axios.delete(`${API}/tracking/${id}`, { withCredentials: true });
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const summary = rows.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1; return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="tracking-dispatches-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-zinc-50 text-2xl font-bold flex items-center gap-2">
            <Truck size={22} className="text-amber-400" /> Tracking Dispatches
          </h1>
          <p className="text-zinc-500 text-xs mt-0.5">
            Twice-daily auto-poll (12:00 + 02:00 IST) · drop in an LR / docket and the system fetches status from the transporter
          </p>
        </div>
        <button onClick={() => setShowForm(s => !s)}
          data-testid="add-dispatch-btn"
          className="flex items-center gap-1.5 px-3 py-2 bg-amber-500 hover:bg-amber-400 text-black text-xs font-semibold rounded-lg">
          <Plus size={14} />{showForm ? 'Close form' : `Add ${tab === 'sales' ? 'sales' : 'purchase'} dispatch`}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-zinc-800">
        {[
          { id: 'sales', label: 'Sales (Outbound)' },
          { id: 'purchase', label: 'Purchase (Inbound)' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`px-4 py-2 text-xs font-semibold border-b-2 transition-colors ${tab === t.id ? 'border-amber-500 text-amber-400' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Summary chips */}
      <div className="flex flex-wrap items-center gap-2">
        {['pending', 'picked_up', 'in_transit', 'out_for_delivery', 'delivered', 'delayed'].map(s => (
          <div key={s} className="flex items-center gap-1.5">
            <StatusChip status={s} />
            <span className="text-zinc-500 text-[11px] font-mono">{summary[s] || 0}</span>
          </div>
        ))}
      </div>

      {showForm && (
        <NewDispatchForm
          kind={tab}
          transporters={transporters}
          onCreated={() => { setShowForm(false); load(); }}
          onCancel={() => setShowForm(false)}
        />
      )}

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-zinc-950 text-zinc-500 uppercase text-[10px]">
            <tr>
              <th className="px-3 py-2 text-left">Transporter / LR</th>
              <th className="px-3 py-2 text-left">Ref</th>
              <th className="px-3 py-2 text-left">{tab === 'sales' ? 'Client' : 'Vendor'}</th>
              <th className="px-3 py-2 text-left">Route</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Last update</th>
              <th className="px-3 py-2 text-left">ETA</th>
              <th className="px-3 py-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-zinc-500">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-12 text-center text-zinc-600">
                <Truck size={28} className="mx-auto mb-2 text-zinc-700" />
                <p>No {tab} dispatches tracked yet — add the first LR/docket above.</p>
              </td></tr>
            )}
            {rows.map(r => (
              <tr key={r.id} className="border-t border-zinc-800 hover:bg-zinc-800/40" data-testid={`tracking-row-${r.id}`}>
                <td className="px-3 py-2 align-top">
                  <div className="flex flex-col">
                    <span className="font-semibold text-zinc-100">{r.transporter_name}</span>
                    <span className="font-mono text-amber-300 text-[11px]">{r.lr_number}</span>
                    {r.tracking_url && (
                      <a href={r.tracking_url} target="_blank" rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 text-[10px] inline-flex items-center gap-1 mt-0.5">
                        Open carrier site <ExternalLink size={9} />
                      </a>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 align-top text-zinc-300 font-mono">{r.ref_label || '—'}</td>
                <td className="px-3 py-2 align-top text-zinc-300">{r.client_or_vendor || '—'}</td>
                <td className="px-3 py-2 align-top">
                  <div className="flex items-center gap-1 text-zinc-400 text-[11px]">
                    <MapPin size={10} className="text-zinc-500" />
                    {r.from_location || '—'} → {r.to_location || '—'}
                  </div>
                  {r.current_location && (
                    <div className="text-amber-300 text-[10px] mt-0.5">Currently: {r.current_location}</div>
                  )}
                </td>
                <td className="px-3 py-2 align-top">
                  <StatusChip status={r.status} />
                  {r.last_status_text && (
                    <div className="text-zinc-500 text-[10px] mt-1 max-w-[200px] line-clamp-2">{r.last_status_text}</div>
                  )}
                </td>
                <td className="px-3 py-2 align-top text-zinc-400 text-[10px]">
                  {r.last_polled_at ? new Date(r.last_polled_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : 'Never'}
                  {r.last_error && (
                    <div className="text-red-400 text-[10px] mt-0.5 max-w-[180px] truncate" title={r.last_error}>⚠ {r.last_error}</div>
                  )}
                </td>
                <td className="px-3 py-2 align-top">
                  {r.expected_delivery && (
                    <div className="flex items-center gap-1 text-zinc-300 text-[11px]">
                      <Calendar size={10} className="text-zinc-500" />
                      {new Date(r.expected_delivery).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                    </div>
                  )}
                  {r.delivered_at && (
                    <div className="text-green-400 text-[10px] mt-0.5">Delivered {new Date(r.delivered_at).toLocaleDateString('en-IN')}</div>
                  )}
                </td>
                <td className="px-3 py-2 align-top">
                  <div className="flex items-center gap-1">
                    <button onClick={() => pollOne(r.id)} disabled={polling === r.id}
                      data-testid={`poll-btn-${r.id}`}
                      title="Re-poll now"
                      className="p-1.5 rounded bg-zinc-800 text-amber-400 hover:bg-amber-900/30 disabled:opacity-50">
                      <RefreshCw size={11} className={polling === r.id ? 'animate-spin' : ''} />
                    </button>
                    {r.status !== 'delivered' && (
                      <button onClick={() => updateStatus(r.id, 'delivered')}
                        title="Mark delivered"
                        className="p-1.5 rounded bg-zinc-800 text-green-400 hover:bg-green-900/30">
                        <CheckCircle2 size={11} />
                      </button>
                    )}
                    <button onClick={() => remove(r.id)}
                      data-testid={`delete-btn-${r.id}`}
                      title="Delete"
                      className="p-1.5 rounded bg-zinc-800 text-red-400 hover:bg-red-900/30">
                      <Trash2 size={11} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
